import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Droplets, Search, Plus, Trash2, Edit2, X, Check, Download, ExternalLink,
  FileText, ChevronDown, ChevronUp, Info, Copy, Save, Printer, Eye,
  AlertTriangle, Settings, ArrowLeft, Building2, Briefcase, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';
import { Wordmark } from '@/components/brand/Brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaintLibrary() {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('keyMadeDarkMode') === 'true');
  const [colors, setColors] = useState([]);
  const [librarySpecs, setLibrarySpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // PL-1 — the picker's own search. It used to share searchTerm with the page
  // bar behind it, so every keystroke in the modal emptied the list below.
  const [colorSearch, setColorSearch] = useState('');
  const [addingColour, setAddingColour] = useState(false);
  const [newColour, setNewColour] = useState({ brand: '', color_code: '', color_name: '', hex_color: '' });
  const [addProblem, setAddProblem] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [brands, setBrands] = useState([]);
  const [finishes, setFinishes] = useState([]);
  const [finishRecommendations, setFinishRecommendations] = useState({});
  const [productLines, setProductLines] = useState({});
  const [applications, setApplications] = useState({});
  const [companySettings, setCompanySettings] = useState(null);
  
  // Dialog states
  const [showSpecEditor, setShowSpecEditor] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [editingSpec, setEditingSpec] = useState(null);
  const [previewSpec, setPreviewSpec] = useState(null);
  
  // Spec editor form
  const [specForm, setSpecForm] = useState({
    color: null,
    sheen: 'Eggshell',
    product_line: '',
    application: 'Interior Walls',
    application_method: 'Roller',
    surface_prep: '',
    primer_required: false,
    primer_product: '',
    // DP26 — no invented figures. These were 2 / 350 / 1 / 4 and 'Low VOC',
    // none of which came from a data sheet. Empty means not recorded.
    coats_recommended: '',
    coverage_sqft_per_gallon: '',
    dry_time_hours: '',
    recoat_time_hours: '',
    tech_data_url: '',
    safety_data_url: '',
    manufacturer_url: '',
    safety_notes: '',
    voc_level: '',
    description: '',
    tags: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (colorSearch || selectedBrand !== 'all') {
      searchColors();
    }
  }, [colorSearch, selectedBrand]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [brandsRes, finishesRes, productLinesRes, applicationsRes, libraryRes, colorsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/paint/brands`),
        axios.get(`${API}/paint/finishes`),
        axios.get(`${API}/paint/product-lines`),
        axios.get(`${API}/paint/applications`),
        axios.get(`${API}/paint/library`),
        axios.get(`${API}/paint/colors`),
        axios.get(`${API}/company/settings`).catch(() => ({ data: null }))
      ]);
      
      setBrands(brandsRes.data.brands);
      setFinishes(finishesRes.data.finishes);
      setFinishRecommendations(finishesRes.data.recommendations);
      setProductLines(productLinesRes.data.product_lines);
      setApplications(applicationsRes.data.applications);
      setLibrarySpecs(libraryRes.data.specs);
      setColors(colorsRes.data.colors);
      if (settingsRes.data) setCompanySettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching paint data:', error);
      toast.error('Failed to load paint data');
    } finally {
      setLoading(false);
    }
  };

  const searchColors = async () => {
    try {
      const params = new URLSearchParams();
      if (colorSearch) params.append('search', colorSearch);
      if (selectedBrand && selectedBrand !== 'all') params.append('brand', selectedBrand);
      
      const response = await axios.get(`${API}/paint/colors?${params.toString()}`);
      setColors(response.data.colors);
    } catch (error) {
      console.error('Error searching colors:', error);
    }
  };

  const openSpecEditor = (spec = null) => {
    if (spec) {
      setEditingSpec(spec);
      setSpecForm({
        color: spec.color,
        sheen: spec.sheen || 'Eggshell',
        product_line: spec.product_line || '',
        application: spec.application || 'Interior Walls',
        application_method: spec.application_method || 'Roller',
        surface_prep: spec.surface_prep || '',
        primer_required: spec.primer_required || false,
        primer_product: spec.primer_product || '',
        coats_recommended: spec.coats_recommended ?? '',
        coverage_sqft_per_gallon: spec.coverage_sqft_per_gallon ?? '',
        dry_time_hours: spec.dry_time_hours ?? '',
        recoat_time_hours: spec.recoat_time_hours ?? '',
        tech_data_url: spec.tech_data_url || '',
        safety_data_url: spec.safety_data_url || '',
        manufacturer_url: spec.manufacturer_url || '',
        safety_notes: spec.safety_notes || '',
        voc_level: spec.voc_level || '',
        description: spec.description || '',
        tags: spec.tags || []
      });
      setShowSpecEditor(true);
    } else {
      setEditingSpec(null);
      setShowColorPicker(true);
    }
  };

  // PL-1 — the firm's own colour: typed once here, found by search ever after.
  const startAddingColour = () => {
    const words = colorSearch.trim().split(/\s+/).filter(Boolean);
    const code = words.find((w) => /\d/.test(w)) || '';
    const brand = words.find((w) => /^(sw|bm|sherwin|benjamin)/i.test(w)) || '';
    const name = words.filter((w) => w !== code && w !== brand).join(' ');
    setNewColour({ brand: brand || '', color_code: code, color_name: name,
      hex_color: '' });
    setAddProblem('');
    setAddingColour(true);
  };

  const saveFirmColour = async () => {
    try {
      const { data } = await axios.post(`${API}/paint/colors`, { ...newColour, source: 'firm' });
      setColors([data.color]);
      setAddingColour(false);
      toast.success(data.already_had_it ? 'That colour was already yours' : 'Added to the firm\'s colours');
      selectColorForNewSpec(data.color);
    } catch (e) {
      setAddProblem(e?.response?.data?.detail || 'That colour did not save');
    }
  };

  const selectColorForNewSpec = (color) => {
    // Auto-generate URLs based on manufacturer
    let manufacturerUrl = '';
    let techDataUrl = '';
    let safetyDataUrl = '';
    
    if (color.brand === 'sherwin_williams') {
      const colorSlug = color.color_name.toLowerCase().replace(/\s+/g, '-');
      const colorCode = color.color_code.replace('SW ', '').trim();
      manufacturerUrl = `https://www.sherwin-williams.com/en-us/color/color-family/neutrals/${colorSlug}-${color.color_code.toLowerCase().replace(' ', '')}`;
      techDataUrl = `https://www.sherwin-williams.com/document/PDS`;
      safetyDataUrl = `https://www.sherwin-williams.com/document/SDS`;
    } else if (color.brand === 'benjamin_moore') {
      const colorSlug = color.color_name.toLowerCase().replace(/\s+/g, '-');
      manufacturerUrl = `https://www.benjaminmoore.com/en-us/paint-colors/color/${color.color_code.toLowerCase().replace(/[^a-z0-9]/g, '-')}/${colorSlug}`;
      techDataUrl = `https://www.benjaminmoore.com/en-us/interior-exterior-paints-stains/product-catalog/rspdf`;
      safetyDataUrl = `https://www.benjaminmoore.com/en-us/interior-exterior-paints-stains/product-catalog/sds`;
    }
    
    setSpecForm({
      ...specForm,
      color: color,
      description: `${color.color_name} (${color.color_code}) - ${color.brand_display}`,
      manufacturer_url: manufacturerUrl,
      tech_data_url: techDataUrl,
      safety_data_url: safetyDataUrl
    });
    setShowColorPicker(false);
    setShowSpecEditor(true);
  };

  const handleSaveSpec = async () => {
    if (!specForm.color) {
      toast.error('Please select a color');
      return;
    }

    try {
      if (editingSpec) {
        await axios.put(`${API}/paint/library/${editingSpec.id}`, specForm);
        toast.success('Paint spec updated');
      } else {
        await axios.post(`${API}/paint/library`, specForm);
        toast.success('Paint spec saved to library');
      }
      
      setShowSpecEditor(false);
      setEditingSpec(null);
      fetchData();
    } catch (error) {
      console.error('Error saving spec:', error);
      toast.error('Failed to save paint spec');
    }
  };

  const handleDeleteSpec = async (specId) => {
    if (!window.confirm('Delete this paint spec from the library?')) return;
    
    try {
      await axios.delete(`${API}/paint/library/${specId}`);
      toast.success('Paint spec deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting spec:', error);
      toast.error('Failed to delete paint spec');
    }
  };

  const openPDFPreview = (spec) => {
    setPreviewSpec(spec);
    setShowPDFPreview(true);
  };

  const handlePrintSpec = async (spec) => {
    try {
      const response = await axios.get(`${API}/paint/library/${spec.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paint_spec_${spec.color?.color_code?.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Get available product lines for selected brand
  const getProductLinesForBrand = () => {
    if (!specForm.color?.brand) return [];
    const brandLines = productLines[specForm.color.brand];
    if (!brandLines) return [];
    
    const appType = specForm.application.includes('Exterior') ? 'exterior' : 
                    specForm.application.includes('Cabinet') || specForm.application.includes('Trim') ? 'specialty' : 'interior';
    return brandLines[appType] || brandLines['interior'] || [];
  };

  // Get recommended sheens for application
  const getRecommendedSheens = () => {
    const app = applications[specForm.application];
    return app?.recommended_sheens || finishes;
  };

  // Large color swatch component
  const ColorSwatch = ({ color, size = 'lg', onClick, showDetails = false }) => {
    const sizeClasses = {
      sm: 'w-10 h-10',
      md: 'w-16 h-16',
      lg: 'w-24 h-24',
      xl: 'w-32 h-32'
    };

    return (
      <div 
        className={`${sizeClasses[size]} rounded-lg border-2 cursor-pointer transition-all hover:scale-105 hover:shadow-lg relative overflow-hidden ${darkMode ? 'border-[#3A3A3A]' : 'border-gray-300'}`}
        style={{ backgroundColor: color.hex_color }}
        onClick={onClick}
        title={`${color.color_name} - ${color.color_code}`}
      >
        {showDetails && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
            <span className="text-white text-[10px] font-medium block truncate">{color.color_code}</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#70170F]"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D] text-[#E5E2DD]' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-40 border-b ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <HelpIcon elementId="paint-library" position="inline" />
                <div>
                  <h1 className="text-xl font-semibold flex items-center gap-2">
                    <Droplets className="w-5 h-5 text-[#70170F]" />
                    Paint Library
                  </h1>
                  <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>
                    Create and manage Paint Spec Sheet templates
                  </p>
                </div>
              </div>
            </div>
            <Button onClick={() => openSpecEditor()} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Plus className="w-4 h-4 mr-2" /> New Paint Spec
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className={`rounded-lg border p-4 mb-6 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-400'}`} />
              <Input
                placeholder="Search library specs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}
              />
            </div>
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger className={`w-full sm:w-48 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}>
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Library Grid */}
        {librarySpecs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {librarySpecs.filter(spec => {
              if (!searchTerm) return true;
              const search = searchTerm.toLowerCase();
              return spec.color?.color_name?.toLowerCase().includes(search) ||
                     spec.color?.color_code?.toLowerCase().includes(search) ||
                     spec.description?.toLowerCase().includes(search);
            }).filter(spec => {
              if (selectedBrand === 'all') return true;
              return spec.color?.brand === selectedBrand;
            }).map((spec) => (
              <div 
                key={spec.id}
                className={`rounded-lg border overflow-hidden ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'}`}
              >
                {/* Color Swatch - Unobstructed */}
                <div 
                  className="h-24"
                  style={{ backgroundColor: spec.color?.hex_color || '#ccc' }}
                />
                
                {/* Spec Info Bar */}
                <div className={`flex items-center justify-between px-3 py-2 ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-100'}`}>
                  <span className={`text-sm font-mono font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-gray-700'}`}>{spec.color?.color_code}</span>
                  <Badge variant="outline" className={darkMode ? 'border-[#3A3A3A]' : ''}>{spec.sheen}</Badge>
                </div>
                
                {/* Content */}
                <div className="p-4">
                  <h3 className={`font-semibold text-lg ${darkMode ? 'text-[#E5E2DD]' : 'text-gray-900'}`}>
                    {spec.color?.color_name}
                  </h3>
                  <p className={`text-sm mb-2 ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-600'}`}>
                    {spec.color?.brand_display}
                  </p>
                  
                  <div className={`text-xs space-y-1 mb-3 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
                    <p>Application: {spec.application}</p>
                    {spec.product_line && <p>Product: {spec.product_line}</p>}
                    <p>Method: {spec.application_method} • {spec.coats_recommended} coats</p>
                    {spec.used_in_projects?.length > 0 && (
                      <p className="text-[#70170F]">Used in {spec.used_in_projects.length} project{spec.used_in_projects.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                  
                  {/* Links */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {spec.manufacturer_url && (
                      <a href={spec.manufacturer_url} target="_blank" rel="noopener noreferrer" 
                         className="text-xs text-[#70170F] hover:underline flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Manufacturer
                      </a>
                    )}
                    {spec.tech_data_url && (
                      <a href={spec.tech_data_url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-[#70170F] hover:underline flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Tech Data
                      </a>
                    )}
                    {spec.safety_data_url && (
                      <a href={spec.safety_data_url} target="_blank" rel="noopener noreferrer"
                         className="text-xs text-amber-600 hover:underline flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> SDS
                      </a>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: darkMode ? '#2A2A2A' : '#e5e7eb' }}>
                    <Button variant="outline" size="sm" onClick={() => openPDFPreview(spec)} className={`flex-1 ${darkMode ? 'border-[#3A3A3A]' : ''}`}>
                      <Eye className="w-4 h-4 mr-1" /> Preview
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handlePrintSpec(spec)} className={darkMode ? 'border-[#3A3A3A]' : ''}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openSpecEditor(spec)} className={darkMode ? 'border-[#3A3A3A]' : ''}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteSpec(spec.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-16 rounded-lg border ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'}`}>
            <Droplets className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-lg font-medium mb-2">No Paint Specs in Library</h3>
            <p className={`text-sm mb-4 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
              Create paint specification templates to use across your projects
            </p>
            <Button onClick={() => openSpecEditor()} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Plus className="w-4 h-4 mr-2" /> Create First Spec
            </Button>
          </div>
        )}
      </div>

      {/* Color Picker Dialog */}
      <Dialog open={showColorPicker} onOpenChange={setShowColorPicker}>
        <DialogContent className={`max-w-4xl max-h-[90vh] ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}>
          <DialogHeader>
            <DialogTitle>Select a Color</DialogTitle>
          </DialogHeader>
          
          {/* Search in dialog */}
          <div className="flex gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder='Brand, code or name — "SW 7069 Iron Ore"' 
                value={colorSearch}
                onChange={(e) => setColorSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onKeyPress={(e) => e.stopPropagation()}
                className={`pl-9 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                data-testid="color-picker-search"
              />
            </div>
            <Select value={selectedBrand} onValueChange={setSelectedBrand}>
              <SelectTrigger className={`w-48 ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}>
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map((brand) => (
                  <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {colors.length === 0 ? (
            <div className="rounded-lg border p-4 mb-3"
              data-testid="color-picker-none">
              <p className="text-sm font-medium">Not in the library yet</p>
              <p className="text-xs mb-3 opacity-70">
                Add it as the firm's own colour and it is found by this search next time.
              </p>
              {addingColour ? (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="color-picker-add-form">
                  <Input placeholder="Brand" value={newColour.brand}
                    onChange={(e) => setNewColour({ ...newColour, brand: e.target.value })}
                    onKeyDown={(e) => e.stopPropagation()}
                    data-testid="color-picker-add-brand" />
                  <Input placeholder="Code" value={newColour.color_code}
                    onChange={(e) => setNewColour({ ...newColour, color_code: e.target.value })}
                    onKeyDown={(e) => e.stopPropagation()}
                    data-testid="color-picker-add-code" />
                  <Input placeholder="Name" value={newColour.color_name}
                    onChange={(e) => setNewColour({ ...newColour, color_name: e.target.value })}
                    onKeyDown={(e) => e.stopPropagation()}
                    data-testid="color-picker-add-name" />
                  <div className="flex gap-2">
                    <Input value={newColour.hex_color}
                      onChange={(e) => setNewColour({ ...newColour, hex_color: e.target.value })}
                      onKeyDown={(e) => e.stopPropagation()}
                      data-testid="color-picker-add-hex" />
                    <input type="color" value={newColour.hex_color}
                      onChange={(e) => setNewColour({ ...newColour, hex_color: e.target.value })}
                      className="h-9 w-10 rounded border"
                      data-testid="color-picker-add-swatch" />
                  </div>
                  {addProblem ? (
                    <p className="text-xs text-red-500 sm:col-span-4" data-testid="color-picker-add-error">{addProblem}</p>
                  ) : null}
                  <div className="sm:col-span-4 flex gap-2">
                    <Button size="sm" onClick={saveFirmColour}
                      data-testid="color-picker-add-save">Add this colour</Button>
                    <Button size="sm" variant="outline" onClick={() => setAddingColour(false)}
                      data-testid="color-picker-add-cancel">Never mind</Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => startAddingColour()}
                  data-testid="color-picker-add">Add this colour</Button>
              )}
            </div>
          ) : null}
          <ScrollArea className="h-96">
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
              {colors.map((color) => (
                <div
                  key={color.id}
                  className={`flex flex-col items-center p-2 rounded-lg cursor-pointer transition-all hover:shadow-lg ${darkMode ? 'hover:bg-[#2A2A2A]' : 'hover:bg-gray-100'}`}
                  onClick={() => selectColorForNewSpec(color)}
                >
                  <ColorSwatch color={color} size="lg" />
                  <p className={`text-xs font-medium mt-1 text-center truncate w-full ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                    {color.color_name}
                  </p>
                  <p className={`text-[10px] text-center ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
                    {color.color_code}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Spec Editor Dialog */}
      <Dialog open={showSpecEditor} onOpenChange={setShowSpecEditor}>
        <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#70170F]" />
              {editingSpec ? 'Edit Paint Spec' : 'Create Paint Spec'}
            </DialogTitle>
          </DialogHeader>
          
          {specForm.color && (
            <div className="space-y-6">
              {/* Color Preview */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#0D0D0D]' : 'bg-gray-50'}`}>
                <div className="flex items-start gap-4">
                  <div 
                    className="w-32 h-32 rounded-lg border-2 flex-shrink-0"
                    style={{ backgroundColor: specForm.color.hex_color, borderColor: darkMode ? '#3A3A3A' : '#e5e7eb' }}
                  />
                  <div className="flex-1">
                    <h4 className={`text-lg font-semibold ${darkMode ? 'text-[#E5E2DD]' : 'text-gray-900'}`}>
                      {specForm.color.color_name}
                    </h4>
                    <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-600'}`}>
                      {specForm.color.color_code} • {specForm.color.brand_display}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline">{specForm.color.hex_color}</Badge>
                      {specForm.color.lrv && <Badge variant="outline">LRV {specForm.color.lrv}</Badge>}
                    </div>
                    <Button variant="link" size="sm" className="p-0 mt-2 text-[#70170F]" onClick={() => { setShowSpecEditor(false); setShowColorPicker(true); }}>
                      Change Color
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Application *</Label>
                  <Select value={specForm.application} onValueChange={(val) => setSpecForm({ ...specForm, application: val })}>
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      {Object.keys(applications).map((app) => (
                        <SelectItem key={app} value={app}>{app}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Sheen *</Label>
                  <Select value={specForm.sheen} onValueChange={(val) => setSpecForm({ ...specForm, sheen: val })}>
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      {finishes.map((finish) => (
                        <SelectItem key={finish} value={finish}>
                          {finish} {getRecommendedSheens().includes(finish) && '★'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Product Line</Label>
                  <Select value={specForm.product_line} onValueChange={(val) => setSpecForm({ ...specForm, product_line: val })}>
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue placeholder="Select product line" />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      {getProductLinesForBrand().map((line) => (
                        <SelectItem key={line.name} value={line.name}>{line.name} ({line.quality})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Application Method</Label>
                  <Select value={specForm.application_method} onValueChange={(val) => setSpecForm({ ...specForm, application_method: val })}>
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      <SelectItem value="Brush">Brush</SelectItem>
                      <SelectItem value="Roller">Roller</SelectItem>
                      <SelectItem value="Spray">Spray</SelectItem>
                      <SelectItem value="Brush & Roller">Brush & Roller</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Coats Recommended</Label>
                  <Select value={String(specForm.coats_recommended)} onValueChange={(val) => setSpecForm({ ...specForm, coats_recommended: parseInt(val) })}>
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      <SelectItem value="1">1 Coat</SelectItem>
                      <SelectItem value="2">2 Coats</SelectItem>
                      <SelectItem value="3">3 Coats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>VOC Level</Label>
                  <Select value={specForm.voc_level} onValueChange={(val) => setSpecForm({ ...specForm, voc_level: val })}>
                    <SelectTrigger className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                      <SelectItem value="Zero VOC">Zero VOC</SelectItem>
                      <SelectItem value="Low VOC">Low VOC</SelectItem>
                      <SelectItem value="Standard">Standard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Technical Data */}
              <div className={`p-4 rounded-lg border ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : 'bg-gray-50 border-gray-200'}`}>
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Technical Data & Links
                </h5>
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <Label className="text-xs">Coverage (sqft/gal)</Label>
                    <Input type="number" value={specForm.coverage_sqft_per_gallon} onChange={(e) => setSpecForm({ ...specForm, coverage_sqft_per_gallon: parseInt(e.target.value) })} className={`h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Dry Time (hrs)</Label>
                    <Input type="number" step="0.5" value={specForm.dry_time_hours} onChange={(e) => setSpecForm({ ...specForm, dry_time_hours: parseFloat(e.target.value) })} className={`h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Recoat Time (hrs)</Label>
                    <Input type="number" step="0.5" value={specForm.recoat_time_hours} onChange={(e) => setSpecForm({ ...specForm, recoat_time_hours: parseFloat(e.target.value) })} className={`h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} />
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Manufacturer Website</Label>
                    <Input placeholder="https://..." value={specForm.manufacturer_url} onChange={(e) => setSpecForm({ ...specForm, manufacturer_url: e.target.value })} className={`h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Tech Data Sheet URL</Label>
                    <Input placeholder="https://..." value={specForm.tech_data_url} onChange={(e) => setSpecForm({ ...specForm, tech_data_url: e.target.value })} className={`h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Safety Data Sheet URL</Label>
                    <Input placeholder="https://..." value={specForm.safety_data_url} onChange={(e) => setSpecForm({ ...specForm, safety_data_url: e.target.value })} className={`h-8 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`} />
                  </div>
                </div>
              </div>

              {/* Safety Notes */}
              <div>
                <Label className="flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> Safety Notes
                </Label>
                <Textarea placeholder="Ventilation requirements, protective equipment, etc." value={specForm.safety_notes} onChange={(e) => setSpecForm({ ...specForm, safety_notes: e.target.value })} className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''} rows={2} />
              </div>

              {/* Description */}
              <div>
                <Label>Description / Notes</Label>
                <Textarea placeholder="Additional notes..." value={specForm.description} onChange={(e) => setSpecForm({ ...specForm, description: e.target.value })} className={darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''} rows={2} />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSpecEditor(false)} className={darkMode ? 'border-[#3A3A3A]' : ''}>Cancel</Button>
            <Button onClick={handleSaveSpec} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Save className="w-4 h-4 mr-1" /> {editingSpec ? 'Update Spec' : 'Save to Library'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={showPDFPreview} onOpenChange={setShowPDFPreview}>
        <DialogContent className={`max-w-2xl ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : ''}`}>
          <DialogHeader>
            <DialogTitle>Paint Spec Sheet Preview</DialogTitle>
          </DialogHeader>
          
          {previewSpec && (
            <div className="bg-white text-gray-900 p-6 rounded-lg">
              {/* Company Header */}
              <div className="border-b-2 border-[#70170F] pb-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-[#70170F]">{companySettings?.name || 'KEY-MADE'}</h1>
                    <p className="text-sm text-gray-600">Paint Specification Sheet</p>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>{new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
              
              {/* Color Info */}
              <div className="flex gap-6 mb-6">
                <div 
                  className="w-40 h-40 rounded-lg border-2 border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: previewSpec.color?.hex_color }}
                />
                <div className="flex-1">
                  <h2 className="text-xl font-bold">{previewSpec.color?.color_name}</h2>
                  <p className="text-lg text-gray-600">{previewSpec.color?.color_code}</p>
                  <p className="text-sm text-gray-500 mt-1">{previewSpec.color?.brand_display}</p>
                  
                  <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                    <div><span className="text-gray-500">Hex:</span> {previewSpec.color?.hex_color}</div>
                    <div><span className="text-gray-500">LRV:</span> {previewSpec.color?.lrv}</div>
                    <div><span className="text-gray-500">Sheen:</span> {previewSpec.sheen}</div>
                    <div><span className="text-gray-500">VOC:</span> {previewSpec.voc_level}</div>
                  </div>
                </div>
              </div>
              
              {/* Specifications */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="font-semibold text-[#70170F] mb-2">Application</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="text-gray-500">Use:</span> {previewSpec.application}</p>
                    <p><span className="text-gray-500">Method:</span> {previewSpec.application_method}</p>
                    <p><span className="text-gray-500">Coats:</span> {previewSpec.coats_recommended}</p>
                    {previewSpec.product_line && <p><span className="text-gray-500">Product:</span> {previewSpec.product_line}</p>}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-[#70170F] mb-2">Technical Data</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="text-gray-500">Coverage:</span> {previewSpec.coverage_sqft_per_gallon} sq ft/gal</p>
                    <p><span className="text-gray-500">Dry Time:</span> {previewSpec.dry_time_hours} hours</p>
                    <p><span className="text-gray-500">Recoat:</span> {previewSpec.recoat_time_hours} hours</p>
                  </div>
                </div>
              </div>
              
              {/* Links */}
              {(previewSpec.manufacturer_url || previewSpec.tech_data_url || previewSpec.safety_data_url) && (
                <div className="mb-6">
                  <h3 className="font-semibold text-[#70170F] mb-2">Resources</h3>
                  <div className="text-sm space-y-1">
                    {previewSpec.manufacturer_url && <p className="text-blue-600 underline">{previewSpec.manufacturer_url}</p>}
                    {previewSpec.tech_data_url && <p className="text-blue-600">Tech Data Sheet Available</p>}
                    {previewSpec.safety_data_url && <p className="text-amber-600">Safety Data Sheet Available</p>}
                  </div>
                </div>
              )}
              
              {/* Safety Notes */}
              {previewSpec.safety_notes && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
                  <h4 className="font-semibold text-amber-800 flex items-center gap-1 mb-1">
                    <AlertTriangle className="w-4 h-4" /> Safety Notes
                  </h4>
                  <p className="text-sm text-amber-900">{previewSpec.safety_notes}</p>
                </div>
              )}
              
              {/* Usage History */}
              {previewSpec.used_in_projects?.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold text-[#70170F] mb-2">Project History</h3>
                  <div className="text-sm text-gray-600">
                    Used in: {previewSpec.used_in_projects.map(p => p.name).join(', ')}
                  </div>
                </div>
              )}
              
              {/* Footer */}
              <div className="border-t pt-4 mt-4 text-xs text-gray-400 text-center space-y-1">
                <p>{companySettings?.name || 'Your Company Name'} • {new Date().toLocaleDateString()}</p>
                <p className="text-gray-300">Engineered by <Wordmark /></p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPDFPreview(false)}>Close</Button>
            <Button onClick={() => handlePrintSpec(previewSpec)} className="bg-[#70170F] hover:bg-[#70170F]/90 text-white">
              <Download className="w-4 h-4 mr-1" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
